import { Grid, Html, Line, OrbitControls } from "@react-three/drei";
import { Canvas, invalidate } from "@react-three/fiber";
import { InfoIcon, MessageBox, Switch } from "@rothoblaas/design-system";
import classNames from "classnames";
import { type FC, Fragment, memo, useEffect, useId, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import * as THREE from "three";
import Icon from "../../../components/ui/Icon";
import useI18n from "../../../hooks/useTranslations";
import { L_SHAPE, RECTANGULAR_SHAPE } from "../../../http/deckDesigner/types/configurations";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import { registerCapturableCanvas } from "../../../utils/webglCanvases";
import { getMeasurementSegments, getSideCode } from "./DeckEdgeMeasurements";
import { getCoordinateBounds, getLShapeDimensions, isMostlyHorizontal } from "./deckGeometry";
import { getFootprintCoordinates, getSlopeArrowCoordinates, getTerrainRiseMmAt, isSlopedTerrain } from "./deckOutline";
import styles from "./DeckSlope3DView.module.css";
import { useMeasurementFormat } from "./measurementFormat";

const TERRAIN_COLOR = "#4a90e2";
const DECK_COLOR = "#a0703c";
const SUPPORT_HEIGHT_COLOR = "#ff7a45";
const UNREACHABLE_COLOR = "#e5484d";
const TOTAL_HEIGHT_COLOR = "#e8e8e8";
const TERRAIN_EDGE_COLOR = "#9a9a9a";
const TERRAIN_HEIGHT_COLOR = "#7fb3f0";
const BACKGROUND_COLOR = "#ffffff";
const SLOPE_ARROW_COLOR = "#e0a000";
// Arrow head: sides as long as this fraction of the arrow, this many degrees off its shaft.
const SLOPE_ARROW_HEAD_RATIO = 0.2;
const SLOPE_ARROW_HEAD_DEGREES = 25;
// The terrain rise can be drawn up to this many times taller, so a gentle slope is visible - view
// only: the whole deck is drawn higher by the extra rise at its highest vertex, so it keeps clearing
// the terrain, while labels, validation and the saved slope keep the real values.
const MAX_SLOPE_EXAGGERATION = 20;
// drei's Html defaults to a huge z-index range, which puts the labels above every modal; keep
// them low and inside the viewport's own stacking context (see .viewport's isolation).
const HTML_Z_INDEX_RANGE: [number, number] = [1, 0];

type Corner = { name: string; x: number; y: number; riseMm: number; supportHeightMm: number };

type WorldPoint = [number, number, number];

const toWorldY = (mm: number) => mm / 1000;

// 3D view of the sloped terrain (blue) under the flat deck (brown). Plan coordinates (x, y) in
// meters map to world (x, elevation, -y), Y-up. The terrain is the plane through the deck's lowest
// vertex (where H applies) rising by slopeDegrees along slopeArrow - the same
// model as calculateWorstCaseSlopeSupportHeightMm, so the corner labels match the support-height
// validation. Orbit/zoom only, the model itself is read-only.
const DeckSlope3DView: FC = () => {
  const i18n = useI18n();
  const unitMm = i18n.translation.common.unitMm;
  const measurementFormat = useMeasurementFormat();
  const calculationTranslation = i18n.translation.deckDesignerModule.configurations.calculation;
  const { control } = useFormContext<DeckDesignerConfigurationFormValues>();
  const [selectedCornerName, setSelectedCornerName] = useState<string | null>(null);
  const [slopeExaggeration, setSlopeExaggeration] = useState(1);
  const [isExaggerationHelpOpen, setIsExaggerationHelpOpen] = useState(false);
  const [isSlopeArrowVisible, setIsSlopeArrowVisible] = useState(true);
  const slopeArrowSwitchId = useId();

  const shape = useWatch({ control, name: "shape", exact: true });
  const base = useWatch({ control, name: "base", exact: true });
  const height = useWatch({ control, name: "height", exact: true });
  const secondaryBase = useWatch({ control, name: "secondaryBase", exact: true });
  const secondaryHeight = useWatch({ control, name: "secondaryHeight", exact: true });
  const totalHeight = useWatch({ control, name: "totalHeight", exact: true });
  const lShapeCutoutCorner = useWatch({ control, name: "lShapeCutoutCorner", exact: true });
  const slopeDegrees = useWatch({ control, name: "slopeDegrees", exact: true });
  const slopeArrow = useWatch({ control, name: "slopeArrow", exact: true });
  const boardThickness = useWatch({ control, name: "boardThickness", exact: true });
  const substructure = useWatch({ control, name: "substructure", exact: true });
  const secondaryItem = useWatch({ control, name: "secondaryItem", exact: true });
  const connector = useWatch({ control, name: "connector", exact: true });

  const selectedShape = shape === RECTANGULAR_SHAPE ? RECTANGULAR_SHAPE : L_SHAPE;
  const coordinates = useMemo(
    () =>
      getFootprintCoordinates({
        shape: selectedShape,
        base,
        height,
        secondaryBase,
        secondaryHeight,
        lShapeCutoutCorner,
      }),
    [base, height, lShapeCutoutCorner, secondaryBase, secondaryHeight, selectedShape],
  );
  const bounds = useMemo(() => getCoordinateBounds(coordinates), [coordinates]);

  const fixedElementsHeightMm =
    (substructure?.height ?? 0) + (secondaryItem?.thickness ?? 0) + (connector?.thickness ?? 0) + (boardThickness ?? 0);

  // Everything at its real values - the exaggeration only affects how it's drawn (see below).
  const scene = useMemo(() => {
    if (!bounds || totalHeight == null) {
      return null;
    }

    const terrainRiseMmAt = getTerrainRiseMmAt(coordinates, { slopeDegrees, slopeArrow });
    // Real rise (world units) at a plan point.
    const riseAt = (x: number, y: number) => toWorldY(terrainRiseMmAt({ x, y }));

    const totalHeightMm = totalHeight * 10;
    const supportHeightBudgetMm = totalHeightMm - fixedElementsHeightMm;

    // Terrain plane spans the bounding box corners.
    const terrainCorners: [number, number][] = [
      [bounds.minX, bounds.minY],
      [bounds.maxX, bounds.minY],
      [bounds.minX, bounds.maxY],
      [bounds.maxX, bounds.maxY],
    ];

    // Numbered like the top view's points (index + 1 over the same outline). The L cutout corner
    // isn't a deck vertex, so it gets no label - no support stands there.
    const corners: Corner[] = coordinates.map((point, index) => {
      const riseMm = terrainRiseMmAt(point);
      return {
        name: String(index + 1),
        x: point.x,
        y: point.y,
        riseMm,
        supportHeightMm: supportHeightBudgetMm - riseMm,
      };
    });

    const size = Math.max(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY);

    // Side names (L1, L2 and, for an L, l1/l2), with the same side codes as the top view,
    // placed just outside each side at deck level.
    const dimensions = getLShapeDimensions(coordinates);
    const sideLabels = getMeasurementSegments(coordinates, coordinates.length).flatMap((segment) => {
      const code = getSideCode(segment, dimensions);
      if (!code) {
        return [];
      }

      const midX = (segment.start.x + segment.end.x) / 2;
      const midY = (segment.start.y + segment.end.y) / 2;
      const isHorizontal = isMostlyHorizontal(segment.start, segment.end);
      // l1/l2 sit on the cutout's inner sides, close to other sides: keep them tighter to their own.
      const offset = size * (code === code.toLowerCase() ? 0.02 : 0.06);

      return [
        {
          code,
          x: isHorizontal ? midX : midX + Math.sign(midX) * offset,
          y: isHorizontal ? midY + Math.sign(midY) * offset : midY,
        },
      ];
    });

    // The downhill direction, as on the top view.
    const terrainSlope = { slopeDegrees, slopeArrow };
    const arrow = isSlopedTerrain(terrainSlope) ? getSlopeArrowCoordinates(coordinates, slopeArrow!) : null;

    return {
      arrow,
      bounds,
      corners,
      maxRiseMm: Math.max(0, ...corners.map((corner) => corner.riseMm)),
      riseAt,
      sideLabels,
      size,
      terrainCorners,
      totalHeightMm,
    };
  }, [bounds, coordinates, fixedElementsHeightMm, slopeDegrees, slopeArrow, totalHeight]);

  // Built at the real rise: the mesh is scaled vertically by the exaggeration instead, the rise being
  // linear - so moving the slider doesn't rebuild it.
  const terrain = useMemo(() => {
    if (!scene) {
      return null;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(
        scene.terrainCorners.flatMap(([x, y]) => [x, scene.riseAt(x, y), -y]),
        3,
      ),
    );
    geometry.setIndex([0, 1, 3, 0, 3, 2]);
    geometry.computeVertexNormals();

    return geometry;
  }, [scene]);

  // The deck is drawn as a slab as thick as the fixed stack (substructure, secondary item,
  // connector, board), from its underside - where h ends - up to its top at H. Without a stack yet
  // (nothing past the shape is set) it's a flat face: a near-zero-thick slab has its top and bottom
  // faces z-fighting, and the deck flickered. Independent of the terrain, so not rebuilt with it.
  const deck = useMemo(() => {
    if (!bounds) {
      return null;
    }

    const deckShape = new THREE.Shape(coordinates.map((point) => new THREE.Vector2(point.x, point.y)));

    return fixedElementsHeightMm > 0
      ? new THREE.ExtrudeGeometry(deckShape, { depth: toWorldY(fixedElementsHeightMm), bevelEnabled: false })
      : new THREE.ShapeGeometry(deckShape);
  }, [bounds, coordinates, fixedElementsHeightMm]);

  // Replaced geometries are freed on the GPU (the meshes don't own them, so nothing else does).
  useEffect(() => () => terrain?.dispose(), [terrain]);
  useEffect(() => () => deck?.dispose(), [deck]);

  // The lines' points, built once per scene/exaggeration: drei's Line rebuilds its geometry whenever
  // it gets a new points array.
  const lines = useMemo(() => {
    if (!scene) {
      return null;
    }

    const { arrow, bounds: sceneBounds, corners, maxRiseMm, riseAt, size, terrainCorners, totalHeightMm } = scene;
    // Drawn rise (world units), exaggerated; the corners' riseMm/supportHeightMm stay the real ones.
    const drawnRiseAt = (x: number, y: number) => riseAt(x, y) * slopeExaggeration;

    // Wireframe "box" under the terrain: its footprint on the grid plus a vertical edge at each
    // corner, so the rise reads against y = 0.
    const terrainEdges: WorldPoint[][] = [
      [
        [sceneBounds.minX, 0, -sceneBounds.minY],
        [sceneBounds.maxX, 0, -sceneBounds.minY],
        [sceneBounds.maxX, 0, -sceneBounds.maxY],
        [sceneBounds.minX, 0, -sceneBounds.maxY],
        [sceneBounds.minX, 0, -sceneBounds.minY],
      ],
      ...terrainCorners.map(([x, y]): WorldPoint[] => [
        [x, 0, -y],
        [x, drawnRiseAt(x, y), -y],
      ]),
    ];

    // The downhill arrow, drawn just above the terrain.
    const toTerrainPoint = (x: number, y: number): WorldPoint => [x, drawnRiseAt(x, y) + size * 0.005, -y];
    const slopeArrowLines = arrow
      ? (() => {
          const shaftAngle = Math.atan2(arrow.end.y - arrow.start.y, arrow.end.x - arrow.start.x);
          const headLength =
            Math.hypot(arrow.end.x - arrow.start.x, arrow.end.y - arrow.start.y) * SLOPE_ARROW_HEAD_RATIO;
          const headSide = (sign: number): WorldPoint => {
            const angle = shaftAngle + Math.PI - sign * ((SLOPE_ARROW_HEAD_DEGREES * Math.PI) / 180);
            return toTerrainPoint(
              arrow.end.x + Math.cos(angle) * headLength,
              arrow.end.y + Math.sin(angle) * headLength,
            );
          };
          const tip = toTerrainPoint(arrow.end.x, arrow.end.y);

          return [
            [toTerrainPoint(arrow.start.x, arrow.start.y), tip],
            [headSide(1), tip, headSide(-1)],
          ];
        })()
      : [];

    // How much higher than its real H the deck is drawn: the extra (exaggerated) rise at the deck's
    // highest vertex, so every support line keeps at least its real length.
    const exaggerationLiftMm = (slopeExaggeration - 1) * maxRiseMm;

    const cornerLines = corners.map((corner) => {
      const groundY = toWorldY(corner.riseMm * slopeExaggeration);

      return {
        corner,
        // h: from the terrain up to the drawn underside of the fixed stack (joist, connector, board).
        supportHeight: [
          [corner.x, groundY, -corner.y],
          [corner.x, toWorldY(corner.riseMm + corner.supportHeightMm + exaggerationLiftMm), -corner.y],
        ] satisfies WorldPoint[],
        // t: terrain rise above the lowest vertex's ground (y = 0), right below the h line.
        terrainHeight: [
          [corner.x, 0, -corner.y],
          [corner.x, groundY, -corner.y],
        ] satisfies WorldPoint[],
      };
    });

    return {
      cornerLines,
      // Drawn deck top: the real H, raised by the exaggeration lift (the label still shows the real H).
      deckY: toWorldY(totalHeightMm + exaggerationLiftMm),
      slopeArrowLines,
      terrainEdges,
    };
  }, [scene, slopeExaggeration]);

  // The canvas only redraws on demand (frameloop="demand"): R3F invalidates for prop changes on scene
  // objects and OrbitControls on every camera move - this covers whatever else a state change touches.
  useEffect(() => {
    invalidate();
  }, [deck, isSlopeArrowVisible, lines, measurementFormat, selectedCornerName, terrain]);

  if (!scene || !lines || !terrain || !deck) {
    return (
      <div style={{ height: 480, display: "grid", placeItems: "center" }}>
        {calculationTranslation.calculationDetailsEmpty}
      </div>
    );
  }

  const { sideLabels, size, totalHeightMm } = scene;
  const { cornerLines, deckY, slopeArrowLines, terrainEdges } = lines;

  return (
    <div>
      <div className={styles.viewport}>
        {/* near scales with the deck like far: depth precision goes with near, and at 0.01 the deck's
            faces (a few mm apart, or the slab's top over the terrain) z-fought and the deck flickered. */}
        <Canvas
          camera={{ position: [size * 0.9, size * 0.7, size * 1.1], fov: 45, near: size * 0.05, far: size * 20 }}
          frameloop="demand"
          onCreated={registerCapturableCanvas}
        >
          <color attach="background" args={[BACKGROUND_COLOR]} />
          <ambientLight intensity={0.8} />
          <directionalLight position={[5, 10, 5]} intensity={0.8} />

          <Grid
            args={[size * 4, size * 4]}
            cellSize={0.5}
            sectionSize={1}
            cellColor="#e4e4e4"
            sectionColor="#c4c4c4"
            infiniteGrid={false}
            position={[0, -0.001, 0]}
          />

          {terrainEdges.map((points, index) => (
            <Line key={index} points={points} color={TERRAIN_EDGE_COLOR} lineWidth={1} transparent opacity={0.8} />
          ))}

          {isSlopeArrowVisible &&
            slopeArrowLines.map((points, index) => (
              <Line key={index} points={points} color={SLOPE_ARROW_COLOR} lineWidth={3} />
            ))}

          {/* Both surfaces write depth and the terrain is drawn first, so where the deck is above the
              terrain it shows on top of it, and where the terrain rises above the deck it hides it -
              without depth writes, transparent meshes just blend in draw order and the terrain always
              looked on top. */}
          <mesh geometry={terrain} scale={[1, slopeExaggeration, 1]} renderOrder={1}>
            <meshStandardMaterial color={TERRAIN_COLOR} transparent opacity={0.55} side={THREE.DoubleSide} />
          </mesh>
          <mesh
            geometry={deck}
            rotation={[-Math.PI / 2, 0, 0]}
            position={[0, deckY - toWorldY(fixedElementsHeightMm), 0]}
            renderOrder={2}
          >
            <meshStandardMaterial color={DECK_COLOR} transparent opacity={0.55} side={THREE.DoubleSide} />
          </mesh>

          {sideLabels.map((side) => (
            <Html
              key={`${side.code}:${side.x}:${side.y}`}
              position={[side.x, deckY, -side.y]}
              center
              zIndexRange={HTML_Z_INDEX_RANGE}
            >
              <div className={styles.sideLabel}>{side.code}</div>
            </Html>
          ))}

          {cornerLines.map(({ corner, supportHeight, terrainHeight }) => {
            const isReachable = corner.supportHeightMm >= 0;
            const isSelected = selectedCornerName === corner.name;

            return (
              <group key={corner.name}>
                {/* h: from the terrain up to the underside of the fixed stack (joist, connector, board). */}
                <Line
                  points={supportHeight}
                  color={isReachable ? SUPPORT_HEIGHT_COLOR : UNREACHABLE_COLOR}
                  lineWidth={2}
                />

                {/* t: terrain rise above the lowest vertex's ground (y = 0), right below the h line. */}
                <Line points={terrainHeight} color={TERRAIN_HEIGHT_COLOR} lineWidth={4} />

                <Html position={[corner.x, deckY, -corner.y]} center zIndexRange={HTML_Z_INDEX_RANGE}>
                  <div
                    role="button"
                    tabIndex={0}
                    aria-pressed={isSelected}
                    className={classNames(styles.cornerLabel, { [styles.cornerLabelSelected]: isSelected })}
                    onClick={() => setSelectedCornerName(isSelected ? null : corner.name)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelectedCornerName(isSelected ? null : corner.name);
                      }
                    }}
                  >
                    {/* One grid row per value (letter | number | unit), so letters and figures line up. */}
                    {[
                      {
                        letter: "h",
                        valueMm: corner.supportHeightMm,
                        color: isReachable ? SUPPORT_HEIGHT_COLOR : UNREACHABLE_COLOR,
                      },
                      ...(isSelected
                        ? [
                            { letter: "t", valueMm: corner.riseMm, color: TERRAIN_HEIGHT_COLOR },
                            { letter: "H", valueMm: totalHeightMm, color: TOTAL_HEIGHT_COLOR },
                          ]
                        : []),
                    ].map((row, index) => (
                      <Fragment key={row.letter}>
                        <span>{index === 0 ? `${corner.name} ·` : ""}</span>
                        <span style={{ color: row.color }}>{row.letter}:</span>
                        <span className={styles.cornerLabelValue} style={{ color: row.color }}>
                          {measurementFormat.length(Math.round(row.valueMm), "mm", `${Math.round(row.valueMm)}`)}
                        </span>
                        {/* The imperial value already carries its inch mark. */}
                        <span style={{ color: row.color }}>{measurementFormat.isImperial ? "" : unitMm}</span>
                      </Fragment>
                    ))}
                  </div>
                </Html>
              </group>
            );
          })}

          <OrbitControls target={[0, deckY / 2, 0]} enablePan={false} makeDefault />
        </Canvas>
        {/* Over the bottom of the viewport, so the help opening above the controls never moves the view. */}
        {isSlopedTerrain({ slopeDegrees, slopeArrow }) && (
          <div className={styles.viewOverlay}>
            {isExaggerationHelpOpen && (
              <MessageBox
                className={styles.exaggerationHint}
                type="informative"
                content={calculationTranslation.slopeViewExaggerationHelp}
              />
            )}
            <div className={styles.slopeControls}>
              <label className={styles.exaggeration}>
                <span>{calculationTranslation.slopeViewExaggeration}</span>
                <button
                  type="button"
                  className={styles.helpButton}
                  aria-expanded={isExaggerationHelpOpen}
                  aria-label={calculationTranslation.slopeViewExaggerationHelp}
                  onClick={(event) => {
                    // Inside the label: keep the click from also focusing the slider.
                    event.preventDefault();
                    setIsExaggerationHelpOpen((isOpen) => !isOpen);
                  }}
                >
                  <Icon glyph={InfoIcon} className={styles.helpIcon} />
                </button>
                <input
                  type="range"
                  min={1}
                  max={MAX_SLOPE_EXAGGERATION}
                  step={1}
                  value={slopeExaggeration}
                  onChange={(event) => setSlopeExaggeration(Number(event.target.value))}
                />
                <span className={styles.exaggerationValue}>×{slopeExaggeration}</span>
              </label>
              <label className={styles.arrowToggle} htmlFor={slopeArrowSwitchId}>
                {calculationTranslation.slopeViewShowArrow}
                <Switch
                  id={slopeArrowSwitchId}
                  checked={isSlopeArrowVisible}
                  onChange={setIsSlopeArrowVisible}
                  color="var(--accent-base, var(--red))"
                />
              </label>
            </div>
          </div>
        )}
      </div>

      <div className={styles.controls}>
        <div className={styles.legend}>
          <div className={styles.legendItem}>
            <span className={styles.swatch} style={{ background: DECK_COLOR }} />
            {calculationTranslation.slopeViewLegendDeck}
          </div>
          <div className={styles.legendItem}>
            <span className={styles.swatch} style={{ background: TERRAIN_COLOR }} />
            {calculationTranslation.slopeViewLegendTerrain}
          </div>
          <div className={styles.legendItem}>
            <span className={styles.lineSwatch} style={{ background: SUPPORT_HEIGHT_COLOR }} />
            {calculationTranslation.slopeViewLegendSupportHeight}
          </div>
          <div className={styles.legendItem}>
            <span className={styles.lineSwatch} style={{ background: TERRAIN_HEIGHT_COLOR }} />
            {calculationTranslation.slopeViewLegendTerrainHeight}
          </div>
          <div className={styles.legendItem}>
            <span className={styles.lineSwatch} style={{ visibility: "hidden" }} />
            {calculationTranslation.slopeViewLegendTotalHeight}
          </div>
        </div>
      </div>
      <MessageBox className={styles.hint} type="informative" content={calculationTranslation.slopeViewClickHint} />
    </div>
  );
};

// Memoized: its props are stable, so it re-renders through its own watched fields and props only, not
// whenever its parent does.
export default memo(DeckSlope3DView);
